/**
 * Application state manager.
 * Replaces the global `state` object from the SPA.
 * State is now backed by API calls instead of localStorage.
 */
import { folderService, worksheetService } from './api';
import { debounce } from './utils';

export interface AppState {
  folders: any[];
  currentFolderId: string | null;
  worksheets: any[];
  currentWorksheet: any | null;
  currentStep: number;
}

const state: AppState = {
  folders: [],
  currentFolderId: null,
  worksheets: [],
  currentWorksheet: null,
  currentStep: 0,
};

type StateListener = (state: AppState) => void;
const _listeners: StateListener[] = [];

/** Subscribe to state changes. */
export function subscribe(listener: StateListener): () => void {
  _listeners.push(listener);
  return () => {
    const idx = _listeners.indexOf(listener);
    if (idx >= 0) _listeners.splice(idx, 1);
  };
}

/** Notify all subscribers of state change. */
function notify(): void {
  for (const listener of _listeners) {
    listener(state);
  }
}

/** Get current state (read-only reference). */
export function getState(): Readonly<AppState> {
  return state;
}

// ─── Folder operations ─────────────────────────────────

export async function loadFolders(): Promise<void> {
  const { folders } = await folderService.list();
  state.folders = folders;
  notify();
}

export async function createFolder(name: string, refId?: string): Promise<any> {
  const { folder } = await folderService.create({ name, ref_id: refId });
  await loadFolders();
  return folder;
}

export async function deleteFolder(id: string): Promise<void> {
  await folderService.delete(id);
  if (state.currentFolderId === id) state.currentFolderId = null;
  await loadFolders();
}

export function setCurrentFolder(id: string | null): void {
  state.currentFolderId = id;
  state.worksheets = [];
  state.currentWorksheet = null;
  notify();
}

// ─── Worksheet operations ──────────────────────────────

export async function loadWorksheets(): Promise<void> {
  if (!state.currentFolderId) return;
  const { worksheets } = await worksheetService.list(state.currentFolderId);
  state.worksheets = worksheets;
  notify();
}

export async function loadWorksheet(id: string): Promise<void> {
  if (!state.currentFolderId) return;
  const { worksheet } = await worksheetService.get(state.currentFolderId, id);
  state.currentWorksheet = worksheet;
  notify();
}

/**
 * Normalize server snake_case → client camelCase on worksheet load.
 * This replaces the api-bridge field mapping that the old SPA used.
 */
function normalizeFromServer(ws: any): any {
  if (!ws) return ws;

  // Top-level fields
  if (ws.trashed_actors && !ws.trashedActors) { ws.trashedActors = ws.trashed_actors; }
  if (ws.template_id && !ws.templateId) { ws.templateId = ws.template_id; }
  if (ws.template_version && !ws.templateVersion) { ws.templateVersion = ws.template_version; }

  // Actors
  (ws.actors || []).forEach((a: any) => {
    if (a.abbr_override !== undefined && a.abbrOverride === undefined) a.abbrOverride = a.abbr_override;
    if (a.badge_color !== undefined && a.badgeColor === undefined) a.badgeColor = a.badge_color;
    if (a.actor_controls !== undefined && a.actorControls === undefined) a.actorControls = a.actor_controls;
    if (a.sort_order !== undefined && a.sortOrder === undefined) a.sortOrder = a.sort_order;
    // Ensure id field exists (SPA uses a.id, server uses a._id)
    if (a._id && !a.id) a.id = a._id.toString();
  });

  // Trashed actors
  (ws.trashedActors || ws.trashed_actors || []).forEach((a: any) => {
    if (a.abbr_override !== undefined && a.abbrOverride === undefined) a.abbrOverride = a.abbr_override;
    if (a.badge_color !== undefined && a.badgeColor === undefined) a.badgeColor = a.badge_color;
    if (a.actor_controls !== undefined && a.actorControls === undefined) a.actorControls = a.actor_controls;
    if (a._id && !a.id) a.id = a._id.toString();
  });

  // Credentials
  (ws.credentials || []).forEach((c: any) => {
    if (c.actor_roles !== undefined && c.actorRoles === undefined) c.actorRoles = c.actor_roles;
    if (c.actor_meta !== undefined && c.actorMeta === undefined) c.actorMeta = c.actor_meta;
    if (c.origin_actor_id !== undefined && c.originActorId === undefined) c.originActorId = c.origin_actor_id;
    if (c.auth_server !== undefined && c.authServer === undefined) c.authServer = c.auth_server;
    if (c.sort_order !== undefined && c.sortOrder === undefined) c.sortOrder = c.sort_order;
    if (c._id && !c.id) c.id = c._id.toString();
  });

  // Controls — server uses control_id, SPA uses id
  (ws.controls || []).forEach((c: any) => {
    if (c.control_id && !c.id) c.id = c.control_id;
    // Ensure cat/label/desc are present (server may not store them)
    if (!c.cat && c.category) c.cat = c.category;
  });

  return ws;
}

/**
 * Normalize client camelCase → server snake_case before save.
 */
function normalizeToServer(ws: any): any {
  if (!ws) return ws;
  const out = { ...ws };

  // Top-level
  out.trashed_actors = ws.trashedActors || ws.trashed_actors || [];
  out.template_id = ws.templateId || ws.template_id || null;
  out.template_version = ws.templateVersion || ws.template_version || null;

  // Actors — preserve _id for credential key stability
  out.actors = (ws.actors || []).map((a: any) => ({
    ...a,
    _id: a._id || a.id,
    abbr_override: a.abbrOverride ?? a.abbr_override ?? null,
    badge_color: a.badgeColor ?? a.badge_color ?? null,
    actor_controls: a.actorControls || a.actor_controls || [],
    sort_order: a.sortOrder ?? a.sort_order ?? 0,
  }));

  // Trashed actors
  out.trashed_actors = (out.trashed_actors || []).map((a: any) => ({
    ...a,
    _id: a._id || a.id,
    abbr_override: a.abbrOverride ?? a.abbr_override ?? null,
    badge_color: a.badgeColor ?? a.badge_color ?? null,
    actor_controls: a.actorControls || a.actor_controls || [],
  }));

  // Credentials
  out.credentials = (ws.credentials || []).map((c: any) => ({
    ...c,
    _id: c._id || c.id,
    actor_roles: c.actorRoles || c.actor_roles || {},
    actor_meta: c.actorMeta || c.actor_meta || {},
    origin_actor_id: c.originActorId || c.origin_actor_id || null,
    auth_server: c.authServer || c.auth_server || '',
    sort_order: c.sortOrder ?? c.sort_order ?? 0,
  }));

  // Controls — map id back to control_id
  out.controls = (ws.controls || []).map((c: any) => ({
    control_id: c.id || c.control_id || '',
    enabled: c.enabled || false,
    recommended: c.recommended || false,
    notes: c.notes || '',
    status: c.status || 'gap',
  }));

  return out;
}

export async function createWorksheet(name?: string, templateId?: string): Promise<any> {
  if (!state.currentFolderId) return;
  const { worksheet } = await worksheetService.create(state.currentFolderId, {
    name,
    template_id: templateId,
  });
  await loadWorksheets();
  return worksheet;
}

/** Debounced save — called on every field change in the wizard. */
export const saveWorksheet = debounce(async () => {
  if (!state.currentFolderId || !state.currentWorksheet) return;
  await worksheetService.update(
    state.currentFolderId,
    state.currentWorksheet._id,
    state.currentWorksheet,
  );
}, 500);

/** Update a field on the current worksheet and trigger debounced save. */
export function updateWorksheetField(field: string, value: unknown): void {
  if (!state.currentWorksheet) return;
  state.currentWorksheet[field] = value;
  notify();
  saveWorksheet();
}

export async function deleteWorksheet(id: string): Promise<void> {
  if (!state.currentFolderId) return;
  await worksheetService.delete(state.currentFolderId, id);
  if (state.currentWorksheet?._id === id) state.currentWorksheet = null;
  await loadWorksheets();
}

export async function duplicateWorksheet(id: string): Promise<void> {
  if (!state.currentFolderId) return;
  await worksheetService.duplicate(state.currentFolderId, id);
  await loadWorksheets();
}

export async function moveWorksheet(id: string, targetFolderId: string): Promise<void> {
  if (!state.currentFolderId) return;
  await worksheetService.move(state.currentFolderId, id, targetFolderId);
  await loadWorksheets();
}

export function setCurrentStep(step: number): void {
  state.currentStep = step;
  notify();
}
